import { BRAND } from '@sixam/config/brand';
import {
  formatRouteDistance,
  formatRouteElevation,
  routePreviewPath,
  routePreviewPathLength,
  type RouteLineString,
} from '@sixam/shared/route-core';
import { Text } from '@sixam/ui/text';
import { cn } from '@sixam/ui/utils';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';
import Svg, { Circle, Line, Path } from 'react-native-svg';

import { AnimatedRoutePath } from '@/components/brand/animated-route-path';

const PREVIEW_WIDTH = 600;
const PREVIEW_HEIGHT = 320;

type RoutePreviewProps = Readonly<{
  className?: string;
  animate?: boolean;
  distanceM?: number;
  elevationGainM?: number | null;
  error?: string | null;
  geometry?: RouteLineString | null;
  isLoading?: boolean;
  name?: string;
  tone?: 'light' | 'dark';
}>;

export function RoutePreview({
  animate = true,
  className,
  distanceM,
  elevationGainM,
  error,
  geometry,
  isLoading = false,
  name,
  tone = 'light',
}: RoutePreviewProps) {
  const isDark = tone === 'dark';
  const routePath = geometry
    ? routePreviewPath(geometry.coordinates, PREVIEW_WIDTH, PREVIEW_HEIGHT, 42)
    : '';
  const hasTelemetry = distanceM != null;

  return (
    <View
      className={cn(
        'overflow-hidden rounded-xl border',
        isDark ? 'border-white/20 bg-neutral-950' : 'border-border bg-card',
        className
      )}
    >
      <RouteCanvas
        animate={animate}
        error={error}
        isDark={isDark}
        isLoading={isLoading}
        name={name}
        routePath={routePath}
      />

      {routePath || hasTelemetry ? (
        <View
          className={cn(
            'flex-row items-end gap-4 border-t px-4 py-3',
            isDark ? 'border-white/20' : 'border-border'
          )}
        >
          <View className="min-w-0 flex-1 gap-1">
            <Text variant="label" className={isDark ? 'text-white/75' : 'text-muted-foreground'}>
              Route
            </Text>
            <Text variant="large" className={isDark ? 'text-white' : undefined} numberOfLines={2}>
              {name || 'Untitled route'}
            </Text>
          </View>
          {distanceM != null ? (
            <RouteMetric label="Distance" tone={tone} value={formatRouteDistance(distanceM)} />
          ) : null}
          {elevationGainM != null ? (
            <RouteMetric label="Climb" tone={tone} value={formatRouteElevation(elevationGainM)} />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function RouteCanvas({
  animate,
  error,
  isDark,
  isLoading,
  name,
  routePath,
}: Readonly<{
  animate: boolean;
  error?: string | null;
  isDark: boolean;
  isLoading: boolean;
  name?: string;
  routePath: string;
}>) {
  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <View className="items-center gap-3">
        <ActivityIndicator color={BRAND.color.orange} />
        <Text variant="label" className={isDark ? 'text-white/75' : 'text-muted-foreground'}>
          Loading route
        </Text>
      </View>
    );
  } else if (error) {
    content = (
      <View className="max-w-xs gap-2 px-6 text-center">
        <Text variant="label" className="text-destructive">
          Route unavailable
        </Text>
        <Text variant="muted" className={isDark ? 'text-white/75' : undefined}>
          {error}
        </Text>
      </View>
    );
  } else if (routePath) {
    content = <RouteTrace animate={animate} isDark={isDark} name={name} routePath={routePath} />;
  } else {
    content = (
      <View className="items-center gap-2 px-6">
        <Text variant="label" className={isDark ? 'text-white' : undefined}>
          No route attached
        </Text>
        <Text variant="muted" className={isDark ? 'text-white/75' : undefined}>
          Add a GPX route to show runners the course.
        </Text>
      </View>
    );
  }

  return (
    <View className="relative h-52 items-center justify-center overflow-hidden">{content}</View>
  );
}

function RouteTrace({
  animate,
  isDark,
  name,
  routePath,
}: Readonly<{ animate: boolean; isDark: boolean; name?: string; routePath: string }>) {
  const first = /^M([\d.]+),([\d.]+)/.exec(routePath);
  const points = routePath.match(/[ML]([\d.]+),([\d.]+)/g) ?? [];
  const last = points.at(-1) ? /[ML]([\d.]+),([\d.]+)/.exec(points.at(-1)!) : null;
  const routeLength = routePreviewPathLength(routePath);

  return (
    <Svg
      accessibilityLabel={name ? `Route trace for ${name}` : 'Route trace'}
      accessibilityRole="image"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      viewBox={`0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}`}
      width="100%"
    >
      {[80, 160, 240, 320, 400, 480, 560].map((x) => (
        <Line
          key={`x-${x}`}
          opacity={isDark ? 0.1 : 0.07}
          stroke={isDark ? BRAND.color.white : BRAND.color.ink}
          strokeWidth={1}
          x1={x}
          x2={x}
          y1={0}
          y2={PREVIEW_HEIGHT}
        />
      ))}
      {[80, 160, 240].map((y) => (
        <Line
          key={`y-${y}`}
          opacity={isDark ? 0.1 : 0.07}
          stroke={isDark ? BRAND.color.white : BRAND.color.ink}
          strokeWidth={1}
          x1={0}
          x2={PREVIEW_WIDTH}
          y1={y}
          y2={y}
        />
      ))}
      <Path
        d={routePath}
        fill="none"
        opacity={0.2}
        stroke={BRAND.color.orange}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={18}
      />
      <AnimatedRoutePath
        d={routePath}
        enabled={animate}
        fill="none"
        length={routeLength}
        stroke={BRAND.color.orange}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={8}
      />
      {first ? <RouteStart isDark={isDark} match={first} /> : null}
      {last ? <RouteFinish isDark={isDark} match={last} /> : null}
    </Svg>
  );
}

function RouteStart({ isDark, match }: Readonly<{ isDark: boolean; match: RegExpExecArray }>) {
  return (
    <>
      <Circle
        cx={Number(match[1])}
        cy={Number(match[2])}
        fill={isDark ? BRAND.color.ink : BRAND.color.white}
        r={13}
        stroke={BRAND.color.orange}
        strokeWidth={5}
      />
      <Circle cx={Number(match[1])} cy={Number(match[2])} fill={BRAND.color.orange} r={4} />
    </>
  );
}

function RouteFinish({ isDark, match }: Readonly<{ isDark: boolean; match: RegExpExecArray }>) {
  return (
    <Circle
      cx={Number(match[1])}
      cy={Number(match[2])}
      fill={isDark ? BRAND.color.white : BRAND.color.ink}
      r={8}
      stroke={BRAND.color.orange}
      strokeWidth={4}
    />
  );
}

function RouteMetric({
  label,
  tone,
  value,
}: Readonly<{ label: string; tone: 'light' | 'dark'; value: string }>) {
  return (
    <View className="items-end gap-1">
      <Text variant="label" className={tone === 'dark' ? 'text-white/75' : 'text-muted-foreground'}>
        {label}
      </Text>
      <Text variant="mono" className={tone === 'dark' ? 'text-white' : undefined}>
        {value}
      </Text>
    </View>
  );
}
